/**
 * Rundown data for the views: TanStack Query option factories over the API
 * client, and the view model presentational components take.
 *
 * THE DATA-HOOK PATTERN. Each read is a pure `…Query(api)` factory returning
 * `queryOptions` (key + fn) and each write a `…Mutation(api, queryClient)`
 * factory returning mutation options — testable without React. The hooks in
 * `hooks.ts` only bind them to the app's backend. Presentational components
 * see neither: a view calls the hook and passes plain props down.
 */
import { allPages, type ApiClient } from '@norna/api-client/client.js';
import type { Row } from '@norna/domain/row.js';
import { type QueryClient, queryOptions } from '@tanstack/react-query';

/** What a rundown list shows. Deliberately not the row: components never learn the replication model. */
export type RundownSummary = { readonly rundownId: string; readonly name: string };

export function toRundownSummary(row: Row): RundownSummary {
  const name = row.f['name'];
  return { rundownId: row.rowId, name: typeof name === 'string' && name !== '' ? name : '(unnamed)' };
}

export const rundownKeys = {
  all: ['rundowns'] as const,
  list: () => [...rundownKeys.all, 'list'] as const,
};

/** Every live rundown, all pages, sorted by name. */
export function rundownsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: rundownKeys.list(),
    queryFn: async (): Promise<RundownSummary[]> => {
      const rows = await allPages(async (pageToken) => {
        const page = await api.call('listRundowns', { params: null, query: pageToken === undefined ? {} : { pageToken }, body: null });
        return { items: page.rundowns, nextPageToken: page.nextPageToken };
      });
      const out = rows.map(toRundownSummary);
      return out.sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

/** Create a stand-alone rundown; the list refetches once it lands. */
export function createRundownMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (name: string): Promise<RundownSummary> => {
      const created = await api.call('createRundown', { params: null, query: null, body: { name, playlistId: null, ord: null } });
      return toRundownSummary(created.rundown);
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: rundownKeys.all });
    },
  };
}
